import React, { useRef } from 'react'
import { useReactToPrint } from 'react-to-print'

/**
 * Общая печать документов на термопринтер 80мм (XPrinter).
 * Используется во всех меню раздела "Документы".
 */
export const THERMAL_PRINT_PAGE_STYLE = `
	@page {
		size: 80mm auto;
		margin: 2mm;
	}
	.thermal-print-info-line {
		line-height: 1.35;
		word-break: break-word;
		margin-bottom: 2px;
	}
	.thermal-print-receipt .custom-cheque-table {
		table-layout: fixed;
		width: 100%;
	}
	.thermal-print-receipt .custom-cheque-table th,
	.thermal-print-receipt .custom-cheque-table td {
		padding-left: 4px;
	}
	.thermal-print-receipt .custom-cheque-table th:first-child,
	.thermal-print-receipt .custom-cheque-table td:first-child {
		padding-left: 0;
	}
	.thermal-print-section-title {
		font-weight: 700;
		margin-bottom: 2px;
	}
	.thermal-print-receipt .custom-cheque-table .thermal-print-total-row td {
		border-top: 1px dashed #000;
		padding-top: 4px;
		font-weight: 700;
	}
	@media print {
		body {
			margin: 0;
			font-family: Arial, Helvetica, sans-serif;
			color: #000;
			-webkit-print-color-adjust: exact;
			print-color-adjust: exact;
		}
		.thermal-print-receipt,
		.thermal-print-receipt * {
			color: #000 !important;
			background: transparent !important;
			-webkit-print-color-adjust: exact;
			print-color-adjust: exact;
		}
		.thermal-print-receipt .cheque-border-bottom {
			border-bottom: 1px solid #000 !important;
		}
		.thermal-print-receipt .custom-cheque-table th {
			border-bottom: 1px solid #000 !important;
			font-weight: 700;
		}
		.thermal-print-receipt .custom-cheque-table tbody tr:first-child td {
			border-top: 1px dashed #000 !important;
			padding-top: 5px;
		}
		.thermal-print-receipt .custom-cheque-table tbody tr:last-child td {
			border-bottom: 1px dashed #000 !important;
			padding-bottom: 5px;
		}
		.thermal-print-receipt .fw-600 {
			font-weight: 700 !important;
		}
	}
`

export function useThermalPrint() {
	const printRef = useRef()

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
		pageStyle: THERMAL_PRINT_PAGE_STYLE,
	})

	return { printRef, handlePrint }
}

export function ThermalPrintButton({ onClick, className = '' }) {
	return (
		<div className={`table-action-button table-action-primary-button ${className}`}
			onClick={onClick}>
			<i className="uil uil-print"></i>
		</div>
	)
}

function PrintInfoLine({ label, value }) {
	if (value === null || value === undefined || value === '') return null

	return (
		<div className="thermal-print-info-line fz-14">
			<span className="fw-600">{label}:</span> {value}
		</div>
	)
}

function PrintSection({ title, columns = [], rows = [], rowClassName, total }) {
	if (!columns.length) return null

	return (
		<div className="cheque-block-2 cheque-border-bottom px-2 mb-2">
			{title &&
				<div className="thermal-print-section-title fz-14">{title}</div>
			}
			<table className="custom-cheque-table w-100 fz-14">
				<thead>
					<tr>
						{columns.map((column, columnIndex) => (
							<th key={columnIndex}
								className={`text-${column.align || 'start'}`}
								style={{ width: column.width, whiteSpace: 'normal', verticalAlign: 'bottom' }}>
								{column.numbered ? <>&#8470; {column.label}</> : column.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, index) => (
						<tr key={index} className={rowClassName ? rowClassName(row, index) : ''}>
							{columns.map((column, columnIndex) => (
								<td key={columnIndex}
									className={`text-${column.align || 'start'}`}
									style={{
										verticalAlign: 'top',
										whiteSpace: column.numbered ? 'break-spaces' : 'nowrap',
									}}>
									{column.numbered && `${index + 1}. `}
									{column.render ? column.render(row, index) : row[column.key]}
								</td>
							))}
						</tr>
					))}
					{total && total.length > 0 &&
						<tr className="thermal-print-total-row">
							{columns.map((column, columnIndex) => (
								<td key={columnIndex}
									className={`text-${column.align || 'start'} fw-600`}
									style={{
										verticalAlign: 'top',
										whiteSpace: column.numbered ? 'break-spaces' : 'nowrap',
									}}>
									{total[columnIndex]}
								</td>
							))}
						</tr>
					}
				</tbody>
			</table>
		</div>
	)
}

/**
 * Скрытый чек 80мм. Печатается через handlePrint из useThermalPrint().
 *
 * @param printRef  ref из useThermalPrint()
 * @param title     заголовок чека
 * @param info      [{ label, value }] — шапка документа
 * @param columns   [{ key, label, width, align, numbered, render }] — колонки одной таблицы
 * @param rows      строки одной таблицы
 * @param sections  [{ title, columns, rows, rowClassName, total }] — если таблиц несколько
 * @param total     строка итогов по колонкам таблицы, например [t('total'), '12', '', '150 000']
 */
function ThermalPrint({
	printRef,
	title,
	info = [],
	columns,
	rows = [],
	rowClassName,
	sections,
	total,
}) {
	const blocks = (sections && sections.length)
		? sections
		: (columns ? [{ columns, rows, rowClassName, total }] : [])

	return (
		<div style={{ display: 'none' }}>
			<div
				ref={printRef}
				className="main w80mm thermal-print-receipt"
				style={{ width: '80mm', color: '#000' }}
			>
				{title &&
					<h4 className="fw-600 mb-2 fz-16">{title}</h4>
				}

				{info.length > 0 &&
					<div className="mb-2">
						{info.map((line, index) => (
							<PrintInfoLine key={index} label={line.label} value={line.value} />
						))}
					</div>
				}

				{blocks.map((block, index) => (
					<PrintSection key={index}
						title={block.title}
						columns={block.columns}
						rows={block.rows || []}
						rowClassName={block.rowClassName}
						total={block.total} />
				))}
			</div>
		</div>
	)
}

export default ThermalPrint
